

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GracenoteContextType } from "context/gracenoteContext";
import { fetchTeamPreferences } from "core/API/gameMasterSports";
import { GripVertical } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { GracenoteTeam, Sport } from "types";
import { useQuery } from "react-query";

const SortableItem = ({
    id,
    children,
}: {
    id: string;
    children: React.ReactNode;
}) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        listStyleType: "auto",
        display: "flex",
        padding: "0px",
        background: "#f5f5f5",
        borderRadius: "8px",
        border: "1px solid rgba(0, 0, 0, 0.1)",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
        alignItems: "stretch",
        justifyContent: "space-between",
    };

    return (
        <li ref={setNodeRef} style={style}>
            <div
                {...attributes}
                {...listeners}
                style={{
                    cursor: "grab",
                    marginRight: 2,
                    background: "#9fb0ed",
                    borderTopLeftRadius: "8px",
                    borderBottomLeftRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <GripVertical className="h-5 w-5 text-gray-600" />
            </div>
            <div style={{ flexGrow: 1, padding: "8px 16px" }}>{children}</div>
        </li>
    );
};

// TeamSelect Component
interface TeamSelectProps {
    sport: any;
    data: GracenoteTeam[];
    values: any["values"];
    onChange: (selectedTeams: GracenoteTeam[]) => void;
}



export const TeamSelect: React.FC<TeamSelectProps> = ({
    sport,
    data,
    values,
    onChange,

}) => {



    // Use useQuery to fetch team preferences once
    const { data: allTeamPreferences = [] } = useQuery(
        ['teamPreferences'],
        fetchTeamPreferences,
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000, // 5 minutes
            retry: false,
        }
    );

    const defaultSelectedTeams = allTeamPreferences?.reduce(
        (acc, iter) => {
            const teamId = iter.team.id;
            const sportId = iter.team.sport.id;

            const currentSportAcc = acc[sportId] || [];

            return {
                ...acc,
                [sportId]: [...currentSportAcc, teamId],
            };
        },
        {} as Record<string, number[]>
    ) || {};

    const [teamPref, setTeamPref] = useState<any[]>([]); // Assuming any type for team preferences
    const [selectedTeams, setSelectedTeams] = useState<GracenoteTeam[]>([]);
    const [newlyAddedTeams, setNewlyAddedTeams] = useState<GracenoteTeam[]>([]); // Track manually added teams

    const handleChange = (selectedOptions: GracenoteTeam[] | null) => {
        const updatedTeams = selectedOptions || [];
        setSelectedTeams(updatedTeams);
        // Propagate changes to the parent via onChange
        onChange(updatedTeams);
    };

    // Filter team preferences based on sport when allTeamPreferences or sport changes
    useEffect(() => {
        if (allTeamPreferences.length > 0) {
            // Filter team preferences based on sport.sportsName
            const filteredPreferences = allTeamPreferences.filter((e) => {
                const team = e.team;
                return team.team_brand_name && team.sport.label === sport.sport.label;
            });

            // Update the `teamPref` state with the filtered preferences
            setTeamPref(filteredPreferences);

            // Map filtered teams to the selectedTeams state
            const filteredTeams = filteredPreferences.map((e) => e.team);
            setSelectedTeams(filteredTeams);
        }
    }, [sport.sportsName, sport.sport.label, allTeamPreferences]); // This ensures it only triggers when sport or data changes

    // Adjust the 'getOptionLabel' to show team_brand_name and keep the value consistent.
    return (
        <Select
            options={data}
            isMulti
            getOptionLabel={(option: GracenoteTeam) => option.team_brand_name}
            getOptionValue={(option: GracenoteTeam) => `${option.id}`} // Keep the value as the team's ID
            value={selectedTeams}
            // onChange={(e: any) => {
            //     handleChange(e); // Update the selected teams state
            //     // Track the newly added teams separately
            //     const addedTeams = e?.filter((team: GracenoteTeam) => !selectedTeams.some((existing) => existing.id === team.id));
            //     if (addedTeams?.length > 0) {
            //         setNewlyAddedTeams((prev) => [...prev, ...addedTeams]); // Add new teams to the tracking state
            //     }
            // }}
            onChange={(e: any) => {
                const selectedTeams = e as GracenoteTeam[]; // Cast to correct type
                handleChange(selectedTeams);
            }}
            placeholder="Select teams"
            classNamePrefix="react-select"
            styles={{
                control: (provided, state) => ({
                    ...provided,
                    minHeight: 40,
                    borderRadius: "0.375rem",
                    borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
                    borderWidth: "1px",
                    boxShadow: state.isFocused ? "0 0 0 1px #3b82f6" : "none",
                    "&:hover": {
                        borderColor: state.isFocused ? "#3b82f6" : "#9ca3af",
                    },
                    backgroundColor: "#ffffff",
                }),
                menu: (provided) => ({
                    ...provided,
                    zIndex: 50,
                    borderRadius: "0.375rem",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                }),
                menuList: (provided) => ({
                    ...provided,
                    maxHeight: "300px",
                    padding: "4px",
                }),
                option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected
                        ? "rgb(68, 99, 136)"
                        : state.isFocused
                        ? "#f3f4f6"
                        : "#ffffff",
                    color: state.isSelected ? "#ffffff" : "#374151",
                    cursor: "pointer",
                    "&:active": {
                        backgroundColor: state.isSelected ? "rgb(68, 99, 136)" : "#e5e7eb",
                    },
                }),
                multiValue: (provided) => ({
                    ...provided,
                    backgroundColor: "rgb(68, 99, 136)",
                    borderRadius: "4px",
                }),
                multiValueLabel: (provided) => ({
                    ...provided,
                    color: "#ffffff",
                    fontSize: "14px",
                }),
                multiValueRemove: (provided) => ({
                    ...provided,
                    color: "#ffffff",
                    cursor: "pointer",
                    "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.1)",
                        color: "#ffffff",
                    },
                }),
                placeholder: (provided) => ({
                    ...provided,
                    color: "#9ca3af",
                    fontSize: "14px",
                }),
                input: (provided) => ({
                    ...provided,
                    color: "#374151",
                    fontSize: "14px",
                }),
            }}
        />
    );
};
